import React, { useEffect, useMemo, useState } from 'react';
import { t } from '@lingui/macro';
import moment from 'moment';

import BookingsCalendar from '@rover/components/src/datetime/BookingsCalendar';
import FlatButton from '@rover/components/src/OverflowTextModal/FlatButton';
import Modal from '@rover/components/src/OverflowTextModal/Modal';
import { DEFAULT_LANG } from '@rover/constants/src/i18n/language';
import { DAYCARE, OVERNIGHT_BOARDING } from '@rover/constants/src/translations/services.constants';
import {
  AVAILABILITY_PREFERENCES_TYPE_IDS,
  MONTHS_TO_DISPLAY,
} from '@rover/constants/src/translations/sitterProfilePage.constants';
import { Box, Flex, Heading, Paragraph, Text } from '@rover/kibble/core';
import { useApiPersonPublicCalendarRetrieve } from '@rover/rsdk/src/apiClient/latest/api-person-public-calendar-retrieve';
import type { DailyAvailability } from '@rover/rsdk/src/apiClient/latest/model/dailyAvailability';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import type { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import getDateTimeFormatMapForLang from '@rover/utilities/datetime';
import { filterServicesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import { SitterProfileVersionOptions } from '../../constants';
import type { SitterProfileVersion } from '../../hooks/useRevampProfileContext';
import { isLegacyVersion as checkIsLegacyVersion } from '../../utils/utils';
import { SitterProfileCalendarMonthChangeEvent } from '../RevampAvailabilityCalendar/analyticsEvents';
import { SitterProfileCancellationPolicyClicked } from '../RevampServicesCta/analyticsEvents';
import SimpleServiceSelector from '../ServiceSection/SimpleServiceSelector';

import LastUpdate from './LastUpdate';

const DF = getDateTimeFormatMapForLang(DEFAULT_LANG);

type Props = {
  sitterProfile: EnhancedPersonSitterProfile;
  selectedDate: Date;
  initialService?: EnhancedPersonSitterProfileService;
  language?: string;
  isEU?: boolean;
  numberOfMonths?: number;
  pageVariant?: SitterProfileVersion;
};

const AvailabilityCalendarSection = ({
  sitterProfile,
  selectedDate = moment().toDate(),
  initialService,
  language,
  isEU,
  numberOfMonths = 1,
  pageVariant = SitterProfileVersionOptions.LEGACY,
}: Props): JSX.Element | null => {
  const [date, setDate] = useState<Date>(selectedDate);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const brandPets = useBrandSpecificPets();
  const services = filterServicesBasedOnPetTypes(sitterProfile?.services || [], brandPets).filter(
    (service) => !service.isAway && service.active
  );
  const [service, setService] = useState<EnhancedPersonSitterProfileService | undefined>(
    initialService || services[0]
  );

  const handleServiceChange = (slug): void => {
    setService(services.find((s) => s.slug === slug));
  };

  useEffect(() => {
    setDate(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (initialService) {
      setService(initialService);
    }
  }, [initialService]);

  const handleMonthChange = (month: Date): void => {
    setDate(month);
    Logger.info('sitter_profile_calendar_month_changed');
    emitAnalyticsEvent(new SitterProfileCalendarMonthChangeEvent({ pageVariant }));
  };

  const startDate = moment(date).clone().startOf('month').subtract(7, 'day').format(DF.DATE_ISO);
  const endDate = moment(date)
    .clone()
    .endOf('month')
    .add(1, 'month')
    .add(7, 'day')
    .format(DF.DATE_ISO);

  const { data: personCalendar } = useApiPersonPublicCalendarRetrieve(sitterProfile.opk, {
    startDate: startDate.toString(),
    endDate: endDate.toString(),
    serviceType: service?.slug,
  });

  const { availableDates, notAvailableDates } = useMemo(() => {
    const available: Date[] = [];
    const notAvailable: Date[] = [];

    const hostingServices = [
      AVAILABILITY_PREFERENCES_TYPE_IDS()[OVERNIGHT_BOARDING().type],
      AVAILABILITY_PREFERENCES_TYPE_IDS()[DAYCARE().type],
    ];

    const isCalendarItemAvailable = (
      calendarItem: DailyAvailability,
      acceptsOneClient: object
    ): boolean | null => {
      if (!calendarItem) return false;
      const calendar = calendarItem.calendars ? calendarItem.calendars[0] : null;
      if (!calendar) return false;

      // only consider the hosting services
      if (hostingServices.includes(calendar.calendarId)) {
        const isExcluded = !acceptsOneClient[calendar.calendarId] && calendar.spacesOccupied !== 0;
        if (isExcluded) return false;
      }
      return (calendar.spacesAvailable || 0) > 0;
    };

    const acceptsOneClientMap = {};
    personCalendar?.preferences?.forEach((p) => {
      acceptsOneClientMap[p.calendarId] = p.acceptsMoreThanOneClient;
    });
    personCalendar?.availability?.forEach((record) => {
      const isAvailable = isCalendarItemAvailable(record, acceptsOneClientMap);
      const datesArray = isAvailable ? available : notAvailable;
      datesArray.push(moment(record.date).toDate());
    });

    return { availableDates: available, notAvailableDates: notAvailable };
  }, [personCalendar]);

  const handleOpenModal = (event): void => {
    event.preventDefault();
    emitAnalyticsEvent(
      new SitterProfileCancellationPolicyClicked({
        providerId: sitterProfile.opk,
        pageVariant: 'legacy',
      })
    );
    setIsModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  if (services.length === 0 || !service) return null;

  const isLegacyVersion = checkIsLegacyVersion(pageVariant);

  return (
    <Flex
      width={['100%']}
      flexDirection="column"
      alignItems="center"
      pt={isLegacyVersion ? '10x' : '0x'}
      sx={{ paddingBottom: isLegacyVersion ? ['40px', '40px', '9px'] : '0px' }}
      data-testid="availabilty-calendar-section"
    >
      {services.length > 1 && isLegacyVersion && (
        <SimpleServiceSelector
          services={services}
          selectedService={service}
          onServiceChange={handleServiceChange}
          providerOpk={sitterProfile.opk}
        />
      )}
      <BookingsCalendar
        language={language}
        availableDates={availableDates}
        notAvailableDates={notAvailableDates}
        startDate={selectedDate}
        fromMonth={moment().toDate()}
        toMonth={moment().add(MONTHS_TO_DISPLAY, 'month').toDate()}
        onMonthChange={handleMonthChange}
        isEU={isEU}
        numberOfMonths={numberOfMonths}
      />
      {isLegacyVersion && (
        <>
          {personCalendar && (
            <LastUpdate
              sitterName={sitterProfile.firstName}
              service={service}
              personCalendar={personCalendar}
            />
          )}
          <Flex width="100%" justifyContent="flex-start" alignItems="center" pt="2x">
            <Text size="100" fontWeight="regular" mr="1x">
              {`${t`${service.displayName} Cancellation Policy:`}`}{' '}
              <FlatButton onClick={handleOpenModal} size="100" fontWeight="regular">
                {service.meta.cancellationPolicyTranslated.toLowerCase()}
              </FlatButton>
            </Text>
          </Flex>
          <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
            <Box width="100%" pb="3x">
              <Heading size="300">{t`Cancellation Policy`}</Heading>
              {/* @ts-expect-error children should not be required if dangerouslySetInnerHTML is used */}
              <Paragraph
                size="200"
                webProps={{
                  dangerouslySetInnerHTML: {
                    __html: `<strong>${service.displayName}: </strong> ${service.meta.cancellationPolicyDesc}`,
                  },
                }}
              />
            </Box>
          </Modal>
        </>
      )}
    </Flex>
  );
};
export default AvailabilityCalendarSection;
